import { compareDecimal, divideDecimal, multiplyDecimal, subtractDecimal } from './decimal.js';
export const PRICE_SCALE = 4;
const PERCENT_BASE = '100';
function isWithinValidity(tier, referenceDate) {
    if (tier.validFrom !== null && tier.validFrom > referenceDate) {
        return false;
    }
    return tier.validTo === null || tier.validTo >= referenceDate;
}
export function applyDiscount(unitPrice, discountPct) {
    if (compareDecimal(discountPct, '0') === 0) {
        return multiplyDecimal(unitPrice, '1', PRICE_SCALE);
    }
    const factor = divideDecimal(subtractDecimal(PERCENT_BASE, discountPct, 6), PERCENT_BASE, 8);
    return multiplyDecimal(unitPrice, factor, PRICE_SCALE);
}
/**
 * Preisfindung als reine Funktion (docs/SPEC.md 3.3): Es gewinnt die Staffel mit der
 * größten `minQuantity ≤ Menge`. Kundengruppenspezifische Staffeln haben Vorrang vor
 * allgemeinen; ohne passende Staffel bleibt der Listenpreis stehen.
 */
export function resolveProductPrice(input) {
    const group = input.priceGroup ?? null;
    const groupOf = (tier) => input.priceGroupId === undefined ? tier.priceGroup : (tier.priceGroupId ?? null);
    const selectedGroup = input.priceGroupId === undefined ? group : input.priceGroupId;
    const candidates = input.tiers.filter((tier) => (tier.customerId === null ||
        tier.customerId === undefined ||
        tier.customerId === input.customerId) &&
        tier.currency === input.currency &&
        // A legacy group without its migrated ID must never become a general condition.
        (input.priceGroupId === undefined ||
            (tier.priceGroupId !== null && tier.priceGroupId !== undefined) ||
            tier.priceGroup === null) &&
        (groupOf(tier) === null || groupOf(tier) === selectedGroup) &&
        (input.baseUnit === undefined ||
            tier.unitCode === undefined ||
            tier.unitCode === input.baseUnit) &&
        compareDecimal(tier.minQuantity, input.quantity) <= 0 &&
        isWithinValidity(tier, input.referenceDate));
    const best = candidates.reduce((winner, tier) => {
        if (winner === null) {
            return tier;
        }
        const tierIsIndividual = tier.customerId !== null && tier.customerId !== undefined;
        const winnerIsIndividual = winner.customerId !== null && winner.customerId !== undefined;
        if (tierIsIndividual !== winnerIsIndividual)
            return tierIsIndividual ? tier : winner;
        const tierIsSpecific = groupOf(tier) !== null;
        const winnerIsSpecific = groupOf(winner) !== null;
        if (tierIsSpecific !== winnerIsSpecific)
            return tierIsSpecific ? tier : winner;
        const byQuantity = compareDecimal(tier.minQuantity, winner.minQuantity);
        if (byQuantity !== 0)
            return byQuantity > 0 ? tier : winner;
        return compareDecimal(tier.unitPrice, winner.unitPrice) < 0 ? tier : winner;
    }, null);
    if (best === null) {
        if (input.listPrice === null)
            throw new RangeError('migration.priceRequired');
        if (input.listCurrency !== undefined && input.listCurrency !== input.currency) {
            throw new RangeError('priceGroups.noCurrencyPrice');
        }
        return {
            unitPrice: input.listPrice,
            discountPct: '0',
            effectivePrice: applyDiscount(input.listPrice, '0'),
            source: 'LIST',
            tierId: null,
            minQuantity: null,
        };
    }
    return {
        unitPrice: best.unitPrice,
        discountPct: best.discountPct,
        effectivePrice: applyDiscount(best.unitPrice, best.discountPct),
        source: 'TIER',
        tierId: best.id,
        minQuantity: best.minQuantity,
    };
}
/** An applicable unknown tier blocks scalar fallback rather than becoming a free price. */
export function resolveSupplierPrice(input) {
    let selected;
    for (const tier of input.tiers) {
        if (tier.currency !== input.currency ||
            tier.unitCode !== input.unitCode ||
            compareDecimal(tier.minQuantity, input.quantity) > 0 ||
            (tier.validFrom !== null && tier.validFrom > input.referenceDate) ||
            (tier.validTo !== null && tier.validTo < input.referenceDate))
            continue;
        if (selected === undefined || compareDecimal(tier.minQuantity, selected.minQuantity) > 0)
            selected = tier;
    }
    return selected === undefined
        ? { unitPrice: input.purchasePrice, tierId: null, minQuantity: null }
        : { unitPrice: selected.unitPrice, tierId: selected.id, minQuantity: selected.minQuantity };
}
/** Deckungsbeitrag in Prozent gegen den Einstandspreis; `null`, wenn kein EK hinterlegt ist. */
export function marginPct(salesPrice, purchasePrice) {
    if (compareDecimal(salesPrice, '0') === 0) {
        return null;
    }
    const margin = subtractDecimal(salesPrice, purchasePrice, PRICE_SCALE);
    return multiplyDecimal(divideDecimal(margin, salesPrice, 8), PERCENT_BASE, 4);
}
//# sourceMappingURL=pricing.js.map